import { useMemo } from 'react';
import * as React from 'react';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { INTERNAL_LINKS } from '../../utils';
import { Button } from '../design-system';

export const GridItem = styled.div<{ $small?: boolean }>`
  display: flex;
  position: relative;
  flex: 1 1 0;
  align-items: flex-start;
  flex-direction: column;
  margin: 0;
  height: 200px;
  background-color: ${({ theme }) => rgba(theme.backgroundColor, 0.75)};

  ${({ $small, theme }) =>
    !$small
      ? css`
          @media (max-width: ${theme.mediaQuery.LARGE}) {
            height: 180px;
          }

          @media (max-width: ${theme.mediaQuery.MEDIUM}) {
            height: 150px;
          }

          @media (max-width: ${theme.mediaQuery.SMALL}) {
            height: 120px;
          }
        `
      : css`
          height: 80px;
        `}

  > a {
    display: flex;

    > div {
      width: 100%;
      min-width: 0;

      > div {
        width: 100%;
        min-width: 0;
        height: 100%;
      }
    }
  }
`;

const HoverInfo = styled.div`
  position: absolute;
  top: 50%;
  margin-top: -11px;
  transform: translateY(35px);
  opacity: 0;
  transition:
    opacity ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING},
    transform ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING};
`;

export const GridItemButton = styled(Button)<{
  $showParentConnectionOnHover?: boolean;
}>`
  &&& {
    width: 100%;
    height: 100%;
    border: 1px solid ${({ theme }) => rgba(theme.colors.LIGHT_PURPLE, 0.3)};
    padding: 0;

    ${({ $showParentConnectionOnHover }) =>
      $showParentConnectionOnHover &&
      css`
        img {
          transition: transform ${({ theme }) => theme.animation.DURATION}
            ${({ theme }) => theme.animation.EASING};
        }

        &:hover {
          img {
            transform: translateY(-15px);
          }

          ${HoverInfo} {
            transform: translateY(20px);
            opacity: 1;
          }
        }
      `}
  }
`;

export const GridItemInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  justify-content: center;
`;

export interface IntegrationItem {
  categorySection: {
    name: string;
    order: number;
  };
  logo: {
    url: string;
  };
  name: string;
  slug: string;
}

export interface ThirdPartyIntegrationItem {
  integration: IntegrationItem[];
  logo: {
    url: string;
  };
  name: string;
  url: string;
}

interface IntegrationGridItemProps {
  className?: string;
  logoUrl: string;
  name: string;
  overrideUrl?: string;
  parentIntegration?: IntegrationItem[];
  slug?: string;
}

export const IntegrationGridItem: React.ComponentType<IntegrationGridItemProps> =
  React.memo(
    ({ className, overrideUrl, logoUrl, name, parentIntegration, slug }) => {
      const hasParentIntegration = parentIntegration !== undefined;
      const showParentConnectionOnHover =
        hasParentIntegration && overrideUrl === undefined;

      const link = useMemo(() => {
        if (hasParentIntegration) {
          if (overrideUrl) {
            return overrideUrl;
          }
          return `${INTERNAL_LINKS.Integrations}${parentIntegration[0].slug}`;
        }
        return `${INTERNAL_LINKS.Integrations}${slug}`;
      }, [hasParentIntegration, slug, overrideUrl, parentIntegration]);

      return (
        <GridItem $small={overrideUrl !== undefined} className={className}>
          <GridItemButton
            $showParentConnectionOnHover={showParentConnectionOnHover}
            link={link}
            stylez="minimal"
            text={
              <GridItemInner>
                <img alt={name} src={logoUrl} />
                {showParentConnectionOnHover && (
                  <HoverInfo>+ {parentIntegration[0].name}</HoverInfo>
                )}
              </GridItemInner>
            }
          />
        </GridItem>
      );
    }
  );
